import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, User, Lock, Eye, EyeOff } from 'lucide-react';
import { getBaseUrl } from '@/lib/api';

interface LoginFormProps {
  onLoginSuccess: (token: string, username: string) => void;
}

const LoginForm = ({ onLoginSuccess }: LoginFormProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      formData.append('grant_type', 'password');

      const response = await fetch(`${getBaseUrl()}/Token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': 'Basic cGF2YW46cGs='
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Invalid credentials');
      }

      const data = await response.json();
      if (data.access_token) {
        // Store refresh token if available
        if (data.refresh_token) {
          localStorage.setItem('refreshToken', data.refresh_token);
        }
        onLoginSuccess(data.access_token, username);
      } else {
        throw new Error('No access token received');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50 p-4 sm:p-6 android-login-container keyboard-aware-container">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(59,130,246,0.05),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(147,51,234,0.05),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_40%,rgba(16,185,129,0.05),transparent_50%)]"></div>
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-sm sm:max-w-md shadow-2xl border-0 bg-white/80 backdrop-blur-sm hover:shadow-3xl transition-all duration-500 z-10 mx-4 android-login-card">
        <CardHeader className="text-center space-y-4 sm:space-y-6 pb-6 sm:pb-8 pt-6 sm:pt-8 px-4 sm:px-6">
          {/* Logo Section */}
          <div className="flex justify-center">
            <div className="relative group">
              <div className="w-72 h-20 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center p-4 group-hover:scale-105">
                {/* STOCK 2 TRACK Text Logo */}
                <div className="flex items-center justify-center space-x-1">
                  <span className="text-white font-bold text-2xl tracking-wide">STOCK</span>
                  <span className="text-orange-400 font-bold text-3xl">2</span>
                  <span className="text-white font-bold text-2xl tracking-wide">TRACK</span>
                </div>
              </div>
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            </div>
          </div>
          
          {/* Welcome Text */}
          <div className="space-y-2">
            <CardTitle className="text-xl sm:text-2xl font-bold text-gray-800">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-gray-600 text-sm sm:text-base">
              Sign in to access your Stock2Track dashboard
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="pb-6 sm:pb-8 px-4 sm:px-8">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Username Field */}
            <div className="space-y-2">
              <Label htmlFor="username" className="text-gray-700 font-medium text-sm">
                Username
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-11 sm:h-12 pl-9 sm:pl-10 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500 focus:ring-2 transition-all duration-300 hover:border-blue-400 text-gray-900 placeholder:text-gray-500 text-sm sm:text-base"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium text-sm">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 sm:h-12 pl-9 sm:pl-10 pr-10 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500 focus:ring-2 transition-all duration-300 hover:border-blue-400 text-gray-900 placeholder:text-gray-500 text-sm sm:text-base"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  {showPassword ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 sm:p-4 rounded-lg border border-red-200">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></div>
                  <span className="text-xs sm:text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full h-11 sm:h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] text-sm sm:text-lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                  <span className="text-sm sm:text-base">Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </Button>

            {/* Additional Info */}
            <div className="text-center text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4">
              <p>Secure access to your inventory management system</p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginForm;
